// Inventory Service - Bridges received inventory to order fulfillment
// Maintains FDA compliance audit trails from receiving to shipping

import { auditService } from './auditService';

const RECEIVED_INVENTORY_KEY = 'received_inventory';
const ALLOCATED_INVENTORY_KEY = 'allocated_inventory';

export const inventoryService = {
  // Find a specific SKU in inventory (validates SKU exists)
  findSKU: (skuNumber) => {
    try {
      const receivedInventory = JSON.parse(localStorage.getItem(RECEIVED_INVENTORY_KEY) || '[]');
      const allocatedInventory = JSON.parse(localStorage.getItem(ALLOCATED_INVENTORY_KEY) || '{}');
      
      // Search for the SKU in received inventory
      const inventoryItem = receivedInventory.find(item => 
        item.sku === skuNumber || item.sku === skuNumber.toUpperCase()
      );
      
      if (!inventoryItem) {
        return null; // SKU not found
      }

      // Calculate availability
      const allocated = allocatedInventory[inventoryItem.sku] || 0;
      const available = Math.max(0, (inventoryItem.quantityReceived || 1) - allocated);
      
      // Determine status
      let status = 'AVAILABLE';
      if (!inventoryItem.availableForSale) {
        status = 'NOT_APPROVED';
      } else if (inventoryItem.auditTrail?.shippedAt) {
        status = 'SOLD';
      } else if (inventoryItem.auditTrail?.packedAt) {
        status = 'ALLOCATED';
      } else if (inventoryItem.auditTrail?.soldTo) {
        status = 'RESERVED';
      } else if (available <= 0) {
        status = 'UNAVAILABLE';
      }

      return {
        sku: inventoryItem.sku,
        productId: inventoryItem.productId,
        productName: inventoryItem.productName,
        status: status,
        available: available,
        allocated: allocated,
        lotNumber: inventoryItem.lotNumber,
        expirationDate: inventoryItem.expirationDate,
        location: inventoryItem.auditTrail?.location || 'Main Warehouse',
        orderNumber: inventoryItem.auditTrail?.soldTo || null,
        batchId: inventoryItem.id,
        organization: inventoryItem.organization,
        qcStatus: inventoryItem.qcStatus,
        auditTrail: inventoryItem.auditTrail
      };
    } catch (error) {
      console.error('Error finding SKU:', error);
      return null;
    }
  },

  // Get all available inventory for picking (from completed receiving workflows)
  getAvailableInventory: (organization = null) => {
    try {
      // Get all inventory that's been received and approved for sale
      const receivedInventory = JSON.parse(localStorage.getItem(RECEIVED_INVENTORY_KEY) || '[]');
      const allocatedInventory = JSON.parse(localStorage.getItem(ALLOCATED_INVENTORY_KEY) || '{}');
      
      // Filter by organization if specified
      let inventory = receivedInventory;
      if (organization) {
        inventory = receivedInventory.filter(item => 
          item.organization?.toUpperCase() === organization.toUpperCase()
        );
      }
      
      // Transform to picking format with availability calculations
      return inventory
        .filter(item => item.availableForSale && item.qcStatus === 'APPROVED')
        .map(item => {
          const allocated = allocatedInventory[item.sku] || 0;
          const available = Math.max(0, item.quantityReceived - allocated);
          
          return {
            sku: item.sku,
            product: item.productName,
            organization: item.organization,
            available: available,
            allocated: allocated,
            total: item.quantityReceived,
            batchId: item.id,
            lotNumber: item.lotNumber,
            expirationDate: item.expirationDate,
            receivedDate: item.receivedDate,
            receivedBy: item.receivedBy,
            location: item.auditTrail?.location || 'Main Warehouse',
            costPrice: item.costPrice,
            // Audit information for FDA compliance
            auditTrail: item.auditTrail
          };
        });
    } catch (error) {
      console.error('Error getting available inventory:', error);
      return [];
    }
  },

  // Add received inventory from completed receiving workflows
  addReceivedInventory: (inventoryBatch) => {
    try {
      const receivedInventory = JSON.parse(localStorage.getItem(RECEIVED_INVENTORY_KEY) || '[]');
      
      // Add the new inventory batch
      const updatedInventory = [...receivedInventory, ...inventoryBatch];
      localStorage.setItem(RECEIVED_INVENTORY_KEY, JSON.stringify(updatedInventory));
      
      // Log inventory addition for audit
      auditService.logAction({
        type: 'INVENTORY_ADDED_TO_AVAILABLE',
        entityType: 'inventory_system',
        entityId: 'system',
        userName: 'System',
        details: `Added ${inventoryBatch.length} inventory items to available stock`,
        newState: { itemCount: inventoryBatch.length, items: inventoryBatch.map(i => i.sku) }
      });

      return { success: true };
    } catch (error) {
      console.error('Error adding received inventory:', error);
      return { success: false, error: error.message };
    }
  },

  // Pick inventory for an order (with FDA audit trail)
  pickInventory: (sku, orderNumber, pickedBy, quantity = 1) => {
    try {
      const availableInventory = inventoryService.getAvailableInventory();
      const inventoryItem = availableInventory.find(item => item.sku === sku);
      
      if (!inventoryItem || inventoryItem.available < quantity) {
        return {
          success: false,
          error: 'Insufficient inventory',
          available: inventoryItem?.available || 0
        };
      }

      // Update allocated inventory
      const allocatedInventory = JSON.parse(localStorage.getItem(ALLOCATED_INVENTORY_KEY) || '{}');
      allocatedInventory[sku] = (allocatedInventory[sku] || 0) + quantity;
      localStorage.setItem(ALLOCATED_INVENTORY_KEY, JSON.stringify(allocatedInventory));

      // FDA Compliance: Log the pick action with full audit trail
      auditService.logAction({
        type: 'INVENTORY_PICKED',
        entityType: 'inventory_batch',
        entityId: inventoryItem.batchId,
        userName: pickedBy,
        userRole: 'Warehouse Staff',
        location: inventoryItem.location,
        details: `Picked ${quantity}x ${sku} for order ${orderNumber}`,
        previousState: {
          available: inventoryItem.available,
          allocated: inventoryItem.allocated
        },
        newState: {
          available: inventoryItem.available - quantity,
          allocated: inventoryItem.allocated + quantity,
          orderNumber: orderNumber
        }
      });

      // Update the audit trail in the inventory record to track the sale
      const receivedInventory = JSON.parse(localStorage.getItem(RECEIVED_INVENTORY_KEY) || '[]');
      const updatedReceivedInventory = receivedInventory.map(item => {
        if (item.sku === sku) {
          return {
            ...item,
            auditTrail: {
              ...item.auditTrail,
              soldTo: orderNumber,
              soldAt: new Date().toISOString(),
              soldBy: pickedBy
            }
          };
        }
        return item;
      });
      localStorage.setItem(RECEIVED_INVENTORY_KEY, JSON.stringify(updatedReceivedInventory));

      return {
        success: true,
        item: inventoryItem,
        newAvailable: inventoryItem.available - quantity,
        newAllocated: inventoryItem.allocated + quantity
      };
    } catch (error) {
      console.error('Error picking inventory:', error);
      return { success: false, error: error.message };
    }
  },

  // Pack inventory for shipping (FDA audit trail continuation)
  packInventory: (sku, orderNumber, packedBy) => {
    try {
      // Update audit trail for packing
      const receivedInventory = JSON.parse(localStorage.getItem(RECEIVED_INVENTORY_KEY) || '[]');
      const updatedReceivedInventory = receivedInventory.map(item => {
        if (item.sku === sku && item.auditTrail?.soldTo === orderNumber) {
          return {
            ...item,
            auditTrail: {
              ...item.auditTrail,
              packedBy: packedBy,
              packedAt: new Date().toISOString()
            }
          };
        }
        return item;
      });
      localStorage.setItem(RECEIVED_INVENTORY_KEY, JSON.stringify(updatedReceivedInventory));

      // Log packing action
      auditService.logAction({
        type: 'INVENTORY_PACKED',
        entityType: 'inventory_batch',
        entityId: sku,
        userName: packedBy,
        userRole: 'Warehouse Staff',
        location: 'Packing Station',
        details: `Packed ${sku} for order ${orderNumber}`,
        newState: { orderNumber, status: 'packed' }
      });

      return { success: true };
    } catch (error) {
      console.error('Error packing inventory:', error);
      return { success: false, error: error.message };
    }
  },

  // Ship inventory (complete FDA audit trail)
  shipInventory: (sku, orderNumber, shippedBy, trackingNumber, carrier) => {
    try {
      // Update audit trail for shipping
      const receivedInventory = JSON.parse(localStorage.getItem(RECEIVED_INVENTORY_KEY) || '[]');
      const updatedReceivedInventory = receivedInventory.map(item => {
        if (item.sku === sku && item.auditTrail?.soldTo === orderNumber) {
          return {
            ...item,
            auditTrail: {
              ...item.auditTrail,
              shippedBy: shippedBy,
              shippedAt: new Date().toISOString(),
              trackingNumber: trackingNumber,
              carrier: carrier
            }
          };
        }
        return item;
      });
      localStorage.setItem(RECEIVED_INVENTORY_KEY, JSON.stringify(updatedReceivedInventory));

      // Log shipping action
      auditService.logAction({
        type: 'INVENTORY_SHIPPED',
        entityType: 'inventory_batch',
        entityId: sku,
        userName: shippedBy,
        userRole: 'Shipping Staff',
        location: 'Shipping Dock',
        details: `Shipped ${sku} for order ${orderNumber} via ${carrier}`,
        newState: { 
          orderNumber, 
          trackingNumber, 
          carrier, 
          status: 'shipped',
          shippedAt: new Date().toISOString()
        }
      });

      return { success: true };
    } catch (error) {
      console.error('Error shipping inventory:', error);
      return { success: false, error: error.message };
    }
  },

  // Get complete audit history for an inventory item (FDA compliance)
  getInventoryAuditHistory: (sku) => {
    try {
      // Get the inventory record
      const receivedInventory = JSON.parse(localStorage.getItem(RECEIVED_INVENTORY_KEY) || '[]');
      const inventoryItem = receivedInventory.find(item => item.sku === sku);
      
      if (!inventoryItem) {
        return { success: false, error: 'Inventory item not found' };
      }

      // Get audit logs for this inventory batch
      const auditLogs = auditService.getEntityAuditLogs('inventory_batch', inventoryItem.id);
      
      // Combine with the audit trail from the inventory record
      const completeHistory = {
        inventoryRecord: inventoryItem,
        auditTrail: inventoryItem.auditTrail,
        systemLogs: auditLogs,
        complianceReport: auditService.generateComplianceReport(inventoryItem.id)
      };

      return { success: true, history: completeHistory };
    } catch (error) {
      console.error('Error getting inventory audit history:', error);
      return { success: false, error: error.message };
    }
  },

  // Clear allocated inventory when order is cancelled
  clearAllocation: (sku, quantity = 1) => {
    try {
      const allocatedInventory = JSON.parse(localStorage.getItem(ALLOCATED_INVENTORY_KEY) || '{}');
      if (allocatedInventory[sku]) {
        allocatedInventory[sku] = Math.max(0, allocatedInventory[sku] - quantity);
        if (allocatedInventory[sku] === 0) {
          delete allocatedInventory[sku];
        }
        localStorage.setItem(ALLOCATED_INVENTORY_KEY, JSON.stringify(allocatedInventory));
      }
      return { success: true };
    } catch (error) {
      console.error('Error clearing allocation:', error);
      return { success: false, error: error.message };
    }
  }
};
